import { useTranslation } from 'react-i18next';
import { Lightbulb } from 'lucide-react';
import { CardSkeleton } from '@/components/ui/Skeleton';
import type { BiddingRecommendation } from '@/lib/insightsApi';

interface Props {
  data?: BiddingRecommendation;
  isLoading?: boolean;
}

export function BiddingRecommendationCard({ data, isLoading }: Props) {
  const { t } = useTranslation();

  if (isLoading) return <CardSkeleton />;
  if (!data) return null;

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600">
          <Lightbulb size={16} />
        </div>
        <h3 className="text-sm font-semibold dark:text-white">
          {t('insights.bidding_recommendation.title')}
        </h3>
      </div>

      <div className="mb-3">
        <span className="inline-block px-2.5 py-1 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 text-xs font-semibold rounded-lg">
          {data.recommended_strategy}
        </span>
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
        {data.rationale}
      </p>

      {data.alternatives.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2">
            {t('insights.bidding_recommendation.alternatives')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.alternatives.map((alt, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded-full"
              >
                {alt}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
